import { Inject, Injectable } from '@angular/core';
import { ContactModel } from '@boxx/contacts-core';
import { IHttpBasicResponse } from '@boxx/core';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { EventModel } from '../models/events.model';
import {
  EVENTS_REPOSITORY,
  IEventsApiProps,
  IEventsRepository,
  IUpsertEventPayload,
} from '../repositories/IEvents.repository';
import { IEventsService } from './IEvents.service';

@Injectable({
  providedIn: 'root',
})
export class EventsService implements IEventsService<EventModel> {
  constructor(
    @Inject(EVENTS_REPOSITORY) private repository: IEventsRepository
  ) {}

  getEvents(): Observable<EventModel[]> {
    return this.repository.getEvents().pipe(
      map((response) =>
        response.data
          .map((m) => EventModel.fromDataResponse(m))
          // Always sort ASC
          .sort((a, b) => a.datetimeFrom.localeCompare(b.datetimeFrom))
      ),
      catchError((error) => {
        throw error;
      })
    );
  }

  createEvent(event: EventModel): Observable<EventModel> {
    const payload: IUpsertEventPayload = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      datetime_from: event.datetimeFrom,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      datetime_to: event.datetimeTo,
      description: event.description,

      latitude: event.latitude,
      longitude: event.longitude,
      place: event.place,
      title: event.title,
      url: event.url
    };

    if (event.attendees?.length) {
      payload.attendees = event.attendees.map((c: ContactModel) =>
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ({ contact_id: c.id.toString() })
      );
    }

    return this.repository.createEvent(payload).pipe(
      map(
        (response: IHttpBasicResponse<IEventsApiProps>) =>
          new EventModel({
            id: +response.data.id,
            title: response.data.title,
            place: response.data.place,
            latitude: response.data.latitude,
            longitude: response.data.longitude,
            description: response.data.description,
            url: response.data.url,
            datetimeFrom: moment(response.data.datetime_from).format(),
            datetimeTo: moment(response.data.datetime_to).format(),
            attendees: response.data.attendees.map((a) =>
              ContactModel.fromDataResponse({
                id: a.contact_id,
                email: a.email,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                full_name: a.full_name,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                last_name: a.last_name,
                name: a.name,
                phone: a.phone,
              })
            ),
          })
      )
    );
  }

  updateEvent(event: EventModel): Observable<EventModel> {
    const payload: IUpsertEventPayload = {
      title: event.title,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      datetime_from: event.datetimeFrom,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      datetime_to: event.datetimeTo,
      description: event.description,
      latitude: event.latitude,
      longitude: event.longitude,
      place: event.place,
      url: event.url
    };

    if (event.attendees?.length) {
      payload.attendees = event.attendees.map((c: ContactModel) =>
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ({ contact_id: c.id.toString() })
      );
    }

    return this.repository.updateEvent(event.id, payload).pipe(
      map(
        (response: IHttpBasicResponse<IEventsApiProps>) =>
          new EventModel({
            id: +response.data.id,
            title: response.data.title,
            place: response.data.place,
            description: response.data.description,
            url: response.data.url,
            datetimeFrom: moment(response.data.datetime_from).format(),
            datetimeTo: moment(response.data.datetime_to).format(),
            attendees: response.data.attendees.map((a) =>
              ContactModel.fromDataResponse({
                id: a.contact_id,
                email: a.email,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                full_name: a.full_name,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                last_name: a.last_name,
                name: a.name,
                phone: a.phone,
              })
            ),
          })
      ),
      catchError((error) => {
        throw error;
      })
    );
  }

  deleteEvent(payload: number): Observable<boolean> {
    return this.repository.deleteEvent(payload).pipe(
      map((response) => true),
      catchError((error) => {
        throw error;
      })
    );
  }

}
